import { generate, type ToVariants } from '@/components/experiments/Core/generateBasicExperimentComponent';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { useConstantStore, useExperimentStore, useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import { getUserGeoLocation } from '@/helpers/geo-location-helper';
import { CatalogueItem, getAllRawCatalogue, SwiperRule } from '@/helpers/promotion-helper';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { MaybeRefOrGetter, toValue, useMounted } from '@vueuse/core';
import { computed } from 'vue';

// ?preferred_variant=GEO_SWIPER_EXP:control
// ?preferred_variant=GEO_SWIPER_EXP:variant_1
// ?preferred_variant=GEO_SWIPER_EXP:variant_2

// ?remove_preferred_experiment=GEO_SWIPER_EXP

export const GeoSwiperExpName = 'GEO_SWIPER_EXP' as const;

export type GeoSwiperExpVariants = ToVariants<typeof GeoSwiperExpVariants>;

export const GeoSwiperExpVariants = {
	CONTROL: 'control',
	VARIANT_1: 'variant_1',
	VARIANT_2: 'variant_2',
} as const;

export default generate(GeoSwiperExpName, GeoSwiperExpVariants);

export const getSwiperExpTag = (country: string, variant: GeoSwiperExpVariants) => {
	if (variant === 'control') return 'amazon-geosw-co-20';

	const number = ['GB', 'DE'].includes(country) ? '21' : '20';
	const countryCode = ['CA', 'DE'].includes(country) ? `${country.toLowerCase()}-` : '';

	return `amazon-${countryCode}geosw-${variant.replace('ariant_', '')}-${number}`;
};

export const getSwiperExpOffersKey = (country: string | null | undefined) => {
	if (country === 'GB') return 'gbOffers';
	if (country === 'DE') return 'deOffers';
	return 'caOffers';
};

export const geoSwiperCountriesOrder = ['US', 'GB', 'CA', 'DE'];

export const geoExperimentSwiperRules: Record<string, SwiperRule> = {
	variant_1: {
		packages: [9, 9],
		slideDelay: 1000,
		rules: [
			{
				selectors: {
					browsers: [],
				},
				package: 9,
			},
		],
		default: {
			package: 9,
		},
	},
	variant_2: {
		packages: [9, 9, 9, 9],
		slideDelay: 1000,
		rules: [
			{
				selectors: {
					browsers: [],
				},
				package: 9,
			},
		],
		default: {
			package: 9,
		},
	},
};

export function useGeoSwiperExp(title: MaybeRefOrGetter<TitleDetail>) {
	const isMounted = useMounted();
	const { country } = useLanguageStore();
	const { isArbitrating } = useArbitrage();
	const { isLoggedIn } = useUserStore();
	const { allProvidersById } = useConstantStore();
	const { activeVariantsWithControlGroup } = useExperimentStore();

	const geoSwiperLocation = computed(() => {
		if (!isMounted.value || !geoSwiperExpVariant.value) return null;

		return (getUserGeoLocation() ?? [])[0];
	});

	const geoSwiperExpVariant = computed(() => {
		if (
			!isMounted.value ||
			!['US'].includes(country.value) ||
			isLoggedIn.value ||
			isArbitrating.value ||
			toValue(title).offers.some(offer => offer.package.packageId === 15)
		)
			return null;

		return activeVariantsWithControlGroup.value[GeoSwiperExpName] as GeoSwiperExpVariants;
	});

	function geoSwiperOfferFilter(item: CatalogueItem, offerCountry: MaybeRefOrGetter<string>) {
		if (geoSwiperExpVariant.value !== 'variant_2') return false;

		return toValue(offerCountry) === 'CA' && item.packageId === 119;
	}

	function geoSwiperPromotionCatalogue(offerCountry: MaybeRefOrGetter<string>) {
		return new Map<number, CatalogueItem>(
			getAllRawCatalogue({ country, title })!
				.filter(item => !!allProvidersById.value[item.packageId] || toValue(offerCountry) === 'CA')
				.map(item => [item.packageId, item])
		);
	}

	return {
		geoSwiperPromotionCatalogue,
		geoSwiperExpVariant,
		geoSwiperOfferFilter,
		geoSwiperLocation,
	};
}
